import { createContext, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "chartink_theme";

const THEMES = {
  dark: {
    label: "Dark",
    icon: "🌙",
    vars: {},
  },
  midnight: {
    label: "Midnight",
    icon: "🌃",
    vars: {
      "--background": "228 84% 4.5%",
      "--foreground": "0 0% 100%",
      "--card": "228 84% 6%",
      "--card-foreground": "0 0% 100%",
      "--popover": "228 84% 6%",
      "--popover-foreground": "0 0% 100%",
      "--primary": "239 84% 67%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "228 30% 10%",
      "--secondary-foreground": "0 0% 100%",
      "--muted": "228 30% 10%",
      "--muted-foreground": "0 0% 64%",
      "--accent": "228 30% 14%",
      "--accent-foreground": "0 0% 100%",
      "--destructive": "4 100% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "228 30% 14%",
      "--input": "228 30% 14%",
      "--ring": "239 84% 67%",
      "--surface-1": "#0B0D1A",
      "--surface-2": "#11142B",
      "--surface-3": "#1A1D3A",
      "--brand": "#6366F1",
    },
  },
  forest: {
    label: "Forest",
    icon: "🌲",
    vars: {
      "--background": "120 20% 4%",
      "--foreground": "0 0% 100%",
      "--card": "120 20% 6%",
      "--card-foreground": "0 0% 100%",
      "--popover": "120 20% 6%",
      "--popover-foreground": "0 0% 100%",
      "--primary": "142 76% 36%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "120 15% 9%",
      "--secondary-foreground": "0 0% 100%",
      "--muted": "120 15% 9%",
      "--muted-foreground": "0 0% 64%",
      "--accent": "120 15% 13%",
      "--accent-foreground": "0 0% 100%",
      "--destructive": "4 100% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "120 15% 13%",
      "--input": "120 15% 13%",
      "--ring": "142 76% 36%",
      "--surface-1": "#080C08",
      "--surface-2": "#0E160E",
      "--surface-3": "#162216",
      "--brand": "#22C55E",
    },
  },
  lavender: {
    label: "Lavender",
    icon: "💜",
    vars: {
      "--background": "270 20% 4%",
      "--foreground": "0 0% 100%",
      "--card": "270 20% 7%",
      "--card-foreground": "0 0% 100%",
      "--popover": "270 20% 7%",
      "--popover-foreground": "0 0% 100%",
      "--primary": "271 81% 56%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "270 15% 10%",
      "--secondary-foreground": "0 0% 100%",
      "--muted": "270 15% 10%",
      "--muted-foreground": "0 0% 64%",
      "--accent": "270 15% 14%",
      "--accent-foreground": "0 0% 100%",
      "--destructive": "4 100% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "270 15% 14%",
      "--input": "270 15% 14%",
      "--ring": "271 81% 56%",
      "--surface-1": "#0C0812",
      "--surface-2": "#160E22",
      "--surface-3": "#201A30",
      "--brand": "#A855F7",
    },
  },
};

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || "dark";
    } catch {
      return "dark";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {}
    const root = document.documentElement;
    const config = THEMES[theme] || THEMES.dark;
    for (const [key, val] of Object.entries(config.vars)) {
      root.style.setProperty(key, val);
    }
    root.setAttribute("data-theme", theme);
  }, [theme]);

  const setTheme = (t) => {
    if (THEMES[t]) setThemeState(t);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
